import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {baseUrl} from "../comman/config";
import Select from "react-select";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { DateSessionFilter } from "../components/Filter/DateSessionFilter";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from 'xlsx';
import ModalContent from "../components/ModalContent";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import { ItemNameStacked } from '../components/ItemName';
import { reportLinkQuery } from '../components/Filter/reportLink';
import { freshReqRmQty, restorePersonsFromPlanned, buildRecipeExportSheets } from "../components/RecipePlanningTable";
import { eventScopeOrNull } from "../comman/eventScope";


// "Recipe Feedback Report" and "Ingredient Consumed Report" are switched off at the client's
// request, until the reports behind them are ready to be shown. Temporary, so nothing is removed:
// the handlers, the links and the buttons all stay, and putting this back to false brings them
// back exactly as they were.
const REPORT_BUTTONS_DISABLED = true;

function ReceipePlanning() {
  const today = new Date().toISOString().split("T")[0];
  const [mealList, setMealList] = useState([]);
  const [rmDetailForItem, setRMDetailForItem] = useState([]);
  const [maharajName, setMaharajName] = useState('');
  // The recipe's STD RAS -- see Recipe Master. Drives the "100 Persons STD QTY" column.
  const [recipeStdRas, setRecipeStdRas] = useState(null);
  // Who may edit a recipe -- the same roles /receipe/list admits.
  const canEditRecipe = ['0', '1'].includes(String(localStorage.getItem('userRole')));
  const [maharajDetail, setMaharajDetail] = useState('');
  const [itemDisable, setItemDisable] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [ingredientList, setIngredientList] = useState([]);
  const [date, setDate] = useState(today);
  const [session, setSession] = useState("");
  const [formData, setFormData] = useState([]);
  const [processStep, setProcessStep] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false)
  const [showMaharajModal, setShowMaharajModal] = useState(false);
  const [maharajList, setMaharajList] = useState([]);
  const [selectedMaharajId, setSelectedMaharajId] = useState('');
  const [maharajModalItem, setMaharajModalItem] = useState(null);
  const role = localStorage.getItem('userRole');

  const navigate = useNavigate();

  useEffect(() => {
    getIngredientList();
    getMaharajList();
  }, []);

  const getMaharajList = async () => {
    try {
      const response = await axios.get(`${baseUrl}/maharaj/list`);
      setMaharajList(response.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (mealList.length === 0 || activeIndex == null || ingredientList.length === 0) return;

    const itemData = mealList[activeIndex];
    if (!itemData) return;

    const itemId = itemData.menu_item_id;
    const maharajId = itemData.maharaj ?? null;
    // Which event owns this slot, straight off the meal_master row the list was built from.
    // Every table below that is keyed on a date is scoped by it: a replacement event running over
    // the same dates as a disabled one leaves both events' rows on the same day, and without this
    // the screen read whichever it reached first. That is how a new event's Pav Bhaji opened on
    // the PREVIOUS event's saved plan instead of building from Recipe Master.
    const eventScope = eventScopeOrNull(itemData.event);
    const condition = `menu_item_id='${itemId}' AND date = '${date}' AND session='${session.value}'${eventScope}`;
    // Recipe Master is not dated and belongs to no event, so it is not scoped -- it is the
    // standard this screen measures against whichever event is being planned.
    const receipeMasteCondition = `menu_item_id='${itemId}' AND maharaj='${maharajId}'`;

    const fetchDetail = async () => {
      setLoading(true);

      // All data for this menu item fetched in one parallel batch request
      const queries = [
        { key: 'raw_material',           table: 'raw_material',               condition: `date = '${date}' AND session = '${session.value}' AND menu_item_id = '${itemId}'${eventScope}` },
        { key: 'prod_receipe',           table: 'production_receipe_planning', condition },
        { key: 'receipe_master',         table: 'receipe_master',              condition: receipeMasteCondition },
        { key: 'receipe_master_process', table: 'receipe_master_process',      condition: receipeMasteCondition },
        { key: 'receipe_planning',       table: 'receipe_planning',            condition: `${condition} GROUP BY ingredient_id order by id asc` },
      ];
      if (maharajId) {
        queries.push({ key: 'maharaj', table: 'maharaj_master', condition: `id='${maharajId}'` });
      }

      const batchResponse = await axios.post(
        `${baseUrl}/comman/batch-list`,
        { queries },
        { headers: { 'Cache-Control': 'no-cache' } }
      );
      const bd = batchResponse.data.data;

      const rmData         = bd.raw_material ?? [];
      const prpData        = bd.prod_receipe ?? [];
      const recipeData     = bd.receipe_master ?? [];
      const processData    = bd.receipe_master_process ?? [];
      const recipePlanData = bd.receipe_planning ?? [];

      // freeze check — same logic as before
      const freez = prpData.length > 0;
      setItemDisable((role == 0 || role == 1) ? false : freez);
      setRMDetailForItem(rmData);

      // maharaj — same logic as before
      if (maharajId && bd.maharaj?.length > 0) {
        setMaharajName(bd.maharaj[0]?.maharaj_name);
        setMaharajDetail(bd.maharaj[0]);
      }

      const _plannedRmQty = rmData[0]?.planned_rm_qty ?? 0;
      // The recipe's STD RAS, written to every row of the recipe by Recipe Master, so the first
      // one is as good as any. Null on a recipe that has not been given one yet -- the "100
      // Persons STD QTY" column then reads blank rather than 0.000.
      setRecipeStdRas(recipeData?.[0]?.std_ras ?? null);
      // The ras the 100 Persons columns are measured in: the recipe's own STD RAS where Recipe
      // Master has one, and the day's Estimated Ras (100 Persons) where it has not. Zero counts as
      // absent on both. Worked out here because the rows below are built against it.
      const loadStdRas = parseFloat(recipeData?.[0]?.std_ras);
      const loadRas = (!isNaN(loadStdRas) && loadStdRas !== 0)
        ? loadStdRas
        : parseFloat(rmData?.[0]?.ras_qty_per_100_person);
      // A row with nothing planned against it opens on the requirement -- see freshReqRmQty.
      const freshReq = (stdQty) => freshReqRmQty({ stdQty }, { estRM: _plannedRmQty, rasValue: loadRas });
      let ddArray = [];

      // sum consume_qty across all batches per ingredient (production_receipe_planning rows)
      const usedQtyByIngredient = prpData.reduce((acc, p) => {
        const ing = p.ingredient_id;
        const v = parseFloat(p.consume_qty);
        acc[ing] = (acc[ing] || 0) + (isNaN(v) ? 0 : v);
        return acc;
      }, {});

      if (recipePlanData.length > 0 && ingredientList.length > 0) {
        recipePlanData.filter((item) => item && item.ingredient_id !== 9090 && item.ingredient_id !== 9091 && item.ingredient_id !== 9092 && item.ingredient_id !== 9093).forEach((item) => {
          const data = ingredientList.find((v) => v.id == item.ingredient_id);
          const recipeRow = recipeData.find(r => r.ingredient_id == item.ingredient_id);
          // Recipe Master is the source of truth for the qty per 100 kgs, but an ingredient added
          // to this day's plan alone has no master row -- so the day's own saved figure stands in.
          // Without it the row reloads with a blank "100 Persons STD QTY", and every formula on
          // this screen is read from that column.
          const stdQty = recipeRow?.qty ?? (parseFloat(item.std_qty) || 0);
          // Recipe Master is the source of truth for is_main when the ingredient exists in the master recipe.
          // Otherwise (orphaned or removed from master), fall back to the per-day saved value.
          const is_main = recipeRow ? !!recipeRow.is_main : !!item.is_main;
          const planned_qty = (item.planned_qty != null && parseFloat(item.planned_qty) !== 0)
            ? parseFloat(parseFloat(item.planned_qty).toFixed(2))
            : parseFloat((parseFloat(item.required_qty || 0) * _plannedRmQty / 100).toFixed(2));
          const used_qty = parseFloat((usedQtyByIngredient[data.id] || 0).toFixed(2));
          // 100 Persons QTY and Req. RM Qty are read back the way Planned RM Qty is. They are
          // saved now (see sql/receipe_planning_persons_qty.sql), and until they were, a Save put
          // the plan back and left those two -- and % Weightage, which is read off 100 Persons
          // QTY -- sitting at their standard figures beside it. Null on a row saved before that,
          // which is the same "untouched" the two columns already fall back to the STD figure for.
          ddArray.push({ ingredient_id: data.id, name: data.ingredient, unit: data.unit, stdQty, required_qty: item.required_qty, baseQty: 0, store: data.store, db_id: item.id, is_main, planned_qty, used_qty, persons_qty: item.persons_qty ?? null, req_rm_qty: item.req_rm_qty ?? null });
        });

        if (recipeData.length > 0) {
          recipeData.forEach((item) => {
            const isPresent = ddArray.some((ddItem) => ddItem.ingredient_id === item.ingredient_id);
            if (!isPresent) {
              const data = ingredientList.find((v) => v.id == item.ingredient_id);
              const stdQty = parseFloat(item.qty);
              const is_main = item.is_main ? true : false;
              const planned_qty = freshReq(stdQty) ?? parseFloat((stdQty * _plannedRmQty / 100).toFixed(2));
              const used_qty = parseFloat((usedQtyByIngredient[data.id] || 0).toFixed(2));
              ddArray.push({ ingredient_id: data.id, name: data.ingredient, unit: data.unit, stdQty, required_qty: stdQty, baseQty: stdQty, store: data.store, is_main, planned_qty, used_qty, persons_qty: null });
            }
          });
        }

        const receipe_remarks  = { ingredient_id: 9090, name: "receipe_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" };
        const process_remarks  = { ingredient_id: 9091, name: "process_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" };
        const test_remarks     = { ingredient_id: 9092, name: "test_remarks",     unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" };
        const planning_remarks = { ingredient_id: 9093, name: "planning_remarks", unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" };

        const checkRemarks = recipePlanData.filter((item) => item && item.ingredient_id == 9090);
        if (checkRemarks.length > 0) { receipe_remarks.db_id = checkRemarks[0].id; receipe_remarks.remarks = checkRemarks[0].remarks; }
        const processRemarks = recipePlanData.filter((item) => item && item.ingredient_id == 9091);
        if (processRemarks.length > 0) { process_remarks.db_id = processRemarks[0].id; process_remarks.remarks = processRemarks[0].remarks; }
        const testRemarks = recipePlanData.filter((item) => item && item.ingredient_id == 9092);
        if (testRemarks.length > 0) { test_remarks.db_id = testRemarks[0].id; test_remarks.remarks = testRemarks[0].remarks; }
        const planningRemarks = recipePlanData.filter((item) => item && item.ingredient_id == 9093);
        if (planningRemarks.length > 0) { planning_remarks.db_id = planningRemarks[0].id; planning_remarks.remarks = planningRemarks[0].remarks; }

        ddArray.push(test_remarks, process_remarks, receipe_remarks, planning_remarks);

      } else if (recipeData.length > 0 && ingredientList.length > 0) {
        recipeData.forEach((item) => {
          const data = ingredientList.find((v) => v.id == item.ingredient_id);
          const stdQty = parseFloat(item.qty);
          const is_main = item.is_main ? true : false;
          const planned_qty = freshReq(stdQty) ?? parseFloat((stdQty * _plannedRmQty / 100).toFixed(2));
          ddArray.push({ ingredient_id: data.id, name: data.ingredient, unit: data.unit, stdQty, required_qty: stdQty, baseQty: stdQty, store: data.store, is_main, planned_qty, persons_qty: null });
        });

        ddArray.push(
          { ingredient_id: 9092, name: "test_remarks",     unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9091, name: "process_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9090, name: "receipe_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9093, name: "planning_remarks", unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" }
        );
      } else {
        ddArray.push(
          { ingredient_id: 9092, name: "test_remarks",     unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9091, name: "process_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9090, name: "receipe_remarks",  unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" },
          { ingredient_id: 9093, name: "planning_remarks", unit: "", stdQty: "", required_qty: "", baseQty: 0, store: "", remarks: "" }
        );
      }

      // Append any ingredient that has consume_qty in Production Support but is missing from the recipe.
      // Such rows show stdQty/required_qty/planned_qty = 0 and used_qty = summed consume_qty across batches.
      Object.keys(usedQtyByIngredient).forEach((idStr) => {
        const ingredient_id = parseInt(idStr, 10);
        if ([9090, 9091, 9092, 9093].includes(ingredient_id)) return;
        if (ddArray.some((dd) => dd.ingredient_id == ingredient_id)) return;
        const data = ingredientList.find((v) => v.id == ingredient_id);
        if (!data) return;
        const used_qty = parseFloat((usedQtyByIngredient[ingredient_id] || 0).toFixed(2));
        ddArray.push({
          ingredient_id: data.id,
          name: data.ingredient,
          unit: data.unit,
          stdQty: 0,
          required_qty: 0,
          baseQty: 0,
          store: data.store,
          is_main: false,
          planned_qty: 0,
          used_qty,
        });
      });

      setProcessStep(processData);
      // Put 100 Persons QTY and Req. RM Qty back on rows that were saved before they had columns
      // of their own -- the same restoring RM Recipe Planning does on every load, since rm_receipe
      // has nowhere to keep either figure. Without it those rows come back showing the standard
      // 100 Persons QTY and % Weightage beside a Planned RM Qty that is not standard. Rows saved
      // since carry their own figures and are left exactly as they were read.
      //
      // The ras this is measured against is the one the screen itself uses: the recipe's own STD
      // RAS where Recipe Master has one, and the day's Estimated Ras where it has not. Zero counts
      // as absent on both -- see the ras the table is built from.
      setFormData(restorePersonsFromPlanned(ddArray, { estRM: _plannedRmQty, rasValue: loadRas }));
      setLoading(false);
    };

    fetchDetail();
  }, [mealList, activeIndex, ingredientList]);

  // The recipe that was open before the page reloaded.
  //
  // Save, Add Ingredient, Refresh and Maharaj all reload the screen, and which recipe was being
  // worked on is nowhere in the URL -- so the accordion came back closed and the item had to be
  // hunted down the list and clicked again after every single write.
  //
  // Three things were in the way of the mechanism that was here:
  //
  //   - the key was deleted the moment it had been read, so the FIRST reload after a click
  //     reopened the panel and every reload after that -- the Save that usually follows an Add
  //     Ingredient -- did not;
  //   - closing a panel stored the string "null", which Number() turns into NaN, and a NaN
  //     activeIndex is not null, so the screen went looking for mealList[NaN];
  //   - the key was read once on mount, before the day's menu had arrived, and a position into a
  //     list that is not there yet reopens nothing.
  //
  // So the item is remembered by menu_item_id -- the list is rebuilt from the day's menu on every
  // load, and a position means nothing until it is -- and it is looked up once that list lands.
  // The key is screen-specific: the two RM Planning screens keep their own accordions under the
  // shared name 'activeIndex', and an item opened on one of those is not this screen's.
  const OPEN_ITEM_KEY = 'receipePlanningOpenItem';
  const restoredOpenItem = useRef(false);
  // Read during the first render, before any effect can run. The filter submits itself on mount
  // with the day it remembers, and that submit clears this key the way any other change of day
  // does -- so by the time the menu lands the key is gone. What it held is already in hand.
  const [savedOpenItem] = useState(() => localStorage.getItem(OPEN_ITEM_KEY));

  useEffect(() => {
    // Once per page load. A later Submit on the filter is a different day being asked for, and it
    // closes the accordion itself -- see handelFilterChange.
    if (restoredOpenItem.current || mealList.length === 0) return;
    restoredOpenItem.current = true;

    if (!savedOpenItem) return;
    const index = mealList.findIndex((item) => String(item.menu_item_id) === savedOpenItem);
    if (index < 0) return;
    setActiveIndex(index);
    // Put back what the filter's own submit cleared, so the NEXT reload -- the Save that follows
    // the edit this one came back for -- lands on this recipe too.
    localStorage.setItem(OPEN_ITEM_KEY, savedOpenItem);
  }, [mealList, savedOpenItem]);

  const handleClick = (index) => {
    const closing = index === activeIndex;
    setActiveIndex(closing ? null : index);
    if (closing) {
      localStorage.removeItem(OPEN_ITEM_KEY);
    } else {
      localStorage.setItem(OPEN_ITEM_KEY, String(mealList[index]?.menu_item_id ?? ''));
    }
  };

  const getIngredientList = async () => {
    const response = await axios.get(`${baseUrl}/ingredients/list?limit=all`);
    const list = response.data.data.filter((item) => item.status == 1);
    setIngredientList(list);
  };

  const handelFilterChange = async (date, session) => {
    setDate(date);
    setSession(session);
    // A different day or session is a different menu, and the open panel is a position into the
    // old one -- position 3 of the new list is some other dish, which is what stayed open before,
    // details and all. So the accordion closes, and the remembered recipe closes with it rather
    // than the next Save reloading into an item nobody opened.
    setActiveIndex(null);
    localStorage.removeItem(OPEN_ITEM_KEY);
    if (date && session) {
      const condition = ` m.session='${session.value}' AND m.date='${date}'`;
      const response = await axios.get(
        `${baseUrl}/meal_master/item_list?condition=${condition}`
      );
      setMealList(response.data.data);
    }
  };


    // ---- The screen's arithmetic, defined once ----
    //
    // The table renders these and handlePlannedQtyChange has to compute the same figures to
    // answer an edit, so they live here rather than inside the table's own render. Two copies
    // would be two things to keep in step.
    const REMARK_NAMES = ["receipe_remarks", "process_remarks", "test_remarks", "planning_remarks"];
    const estRM = parseFloat(rmDetailForItem?.[0]?.planned_rm_qty ?? 0) || 0;

    // The ras the "100 Persons STD QTY" column is scaled by.
    //
    // The recipe's own STD RAS from Recipe Master, when it has one. It usually has not: std_ras
    // is NULL for every row of receipe_master, so the column came out blank on every recipe --
    // and every formula on this screen divides by that column's total, so with no ras the whole
    // screen sat inert. Typing a 100 Persons QTY moved nothing, because there was nothing to
    // divide by.
    //
    // So it falls back to the day's "Estimated Ras (100 Persons)", shown at the top of this same
    // screen: the same quantity in the same unit, and one that IS filled in. A recipe's own STD
    // RAS still wins the moment someone enters one.
    //
    // Zero counts as absent on both. A ras of nothing scales the column to nothing, which leaves
    // the same unusable divisor as no ras at all.
    const stdRasValue = parseFloat(recipeStdRas);
    const hasStdRas = !isNaN(stdRasValue) && stdRasValue !== 0;
    const estRasValue = parseFloat(rmDetailForItem?.[0]?.ras_qty_per_100_person);
    const rasValue = hasStdRas ? stdRasValue : estRasValue;
    const hasRas = !isNaN(rasValue) && rasValue !== 0;

    // The ingredient rows in the order the table shows them: main items first, then Qty/100kg
    // descending, matching Recipe Master. The change handlers are given an index into THIS list,
    // never into formData -- the two orders are not the same.
    const ingredientRows = formData
      .filter((item) => item && !REMARK_NAMES.includes(item.name))
      .slice()
      .sort((a, b) => {
        if (!!a.is_main !== !!b.is_main) return a.is_main ? -1 : 1;
        return (parseFloat(b.stdQty) || 0) - (parseFloat(a.stdQty) || 0);
      });

    // "100 Persons STD QTY" -- that ras against the ingredient's qty per 100 kgs. Null, shown
    // blank, when neither ras is available: a column of zeroes would read as a measurement rather
    // than as an absence.
    const stdPersonsQty = (row) => {
      const q = parseFloat(row?.stdQty);
      if (!hasRas || isNaN(q)) return null;
      return rasValue * (q / 100);
    };

    // "100 Persons QTY" -- editable, and defaults to the STD figure beside it.
    const personsQty = (row) => {
      if (row?.persons_qty !== null && row?.persons_qty !== undefined && row?.persons_qty !== '') {
        const v = parseFloat(row.persons_qty);
        if (!isNaN(v)) return v;
      }
      return stdPersonsQty(row);
    };

    // Req. RM Qty = Estimated RM Qty * (100 Persons QTY / Estimated Ras (100 Persons)).
    //
    // The ras is the standard's own main-ingredient figure per 100 persons, so Estimated RM Qty
    // over the ras is how many hundreds of persons the day's main ingredient feeds, and each row's
    // requirement is its own per-100-persons figure against that. The main ingredient comes back
    // as the Estimated RM Qty itself -- est * ras / ras -- which is what it is.
    //
    // The divisor used to be the section's own STD total, which made each row a SHARE of the
    // Estimated RM Qty. That reads the same only where a section's quantities add up to 100 per
    // 100 kgs -- which the main ingredients must and the other ingredients need not -- so on a
    // real recipe the requirement jumped the moment anything was typed into 100 Persons QTY, and
    // typing the STD figure straight back landed somewhere else again instead of undoing the edit.
    const reqFromPersons = (row, f) => {
      if (f === null || f === undefined || isNaN(f) || !hasRas) return null;
      return estRM * (f / rasValue);
    };

    // ...and the pair the two editable columns are tied together by:
    //
    //     100 Persons QTY = Planned RM Qty * 100 Persons STD QTY / Req. RM Qty
    //     Planned RM Qty  = 100 Persons QTY * Req. RM Qty / 100 Persons STD QTY
    //
    // One is the rearrangement of the other, so the two edits round-trip: type a 100 Persons QTY,
    // then retype the Planned RM Qty it produced, and the 100 Persons QTY comes back unchanged.
    // Both take Req. RM Qty as an argument rather than reading it, because which Req. RM Qty
    // applies differs by direction -- see the two handlers.
    // An ingredient added to the day's plan alone can have no "100 Persons STD QTY" -- nothing in
    // Recipe Master to read a qty per 100 kgs from, and nothing typed into the modal to stand in
    // for one. Both formulas divide by that figure, so both returned null and the row sat inert:
    // typing in either box moved nothing, while every other row answered the same edit.
    //
    // With no standard to deviate from, the plan IS the requirement, and the requirement is the
    // share formula above. The pair reads that identity in each direction, which is what the two
    // formulas below collapse to once a row's STD figure equals its 100 Persons QTY -- so a row
    // with a standard and a row without behave the same way.
    const personsFromPlanned = (row, l, req) => {
      const s = stdPersonsQty(row);
      if (isNaN(l)) return null;
      if (s === null || !s) {
        // Planned RM Qty = Req. RM Qty = Estimated RM Qty * 100 Persons QTY / ras, rearranged.
        if (!estRM || !hasRas) return null;
        return (l * rasValue) / estRM;
      }
      if (req === null || !req) return null;
      return (l * s) / req;
    };

    // Planned RM Qty, from a typed 100 Persons QTY: the requirement that figure calls for.
    //
    // It used to be 100 Persons QTY * Req. RM Qty / 100 Persons STD QTY, a scaling ON TOP of the
    // requirement -- right while the requirement stayed pinned across the edit, wrong once the
    // edit began restating the requirement as well, because the ratio then landed twice. On મકાઈ
    // તેલ at Est. RM Qty 110 and ras 2.0, typing 1.2 over a STD of 0.600 doubled the requirement
    // to 66 and then doubled that again, planning 132 against a requirement of 66.
    const plannedFromPersons = (row, f, req) => {
      if (f === null || isNaN(f) || req === null) return null;
      return req;
    };

    // The figure the Req. RM Qty column shows.
    //
    // Normally it follows 100 Persons QTY. A Planned RM Qty edit PINS it (req_rm_qty) instead:
    // planning more or less than the requirement must not restate the requirement, and that edit
    // moves 100 Persons QTY, which the requirement is otherwise read from. Editing 100 Persons QTY
    // replaces the pin with the figure that edit calls for, so the column is never stale.
    //
    // A row nobody has typed into reads the STD figure beside it, so this comes out at the
    // standard requirement -- the same figure freshReqRmQty writes into Planned RM Qty, and the
    // same one that typing that STD figure back into the box returns to.
    const reqRmQty = (row) => {
      if (row?.req_rm_qty !== null && row?.req_rm_qty !== undefined && row?.req_rm_qty !== '') {
        const v = parseFloat(row.req_rm_qty);
        if (!isNaN(v)) return v;
      }
      return reqFromPersons(row, personsQty(row));
    };

    const handleIsMainChange = (ingredient_id, checked) => {
        setFormData(prev => prev.map(item =>
            item.ingredient_id === ingredient_id ? { ...item, is_main: checked } : item
        ));
    };

    // "100 Persons QTY" -- editable, and the figure the rest of the row is read from. An edit here
    // moves Req. RM Qty and Planned RM Qty with it (% Weightage follows on its own, being read
    // straight off 100 Persons QTY):
    //
    //     Req. RM Qty    = Estimated RM Qty * 100 Persons QTY / Estimated Ras (100 Persons)
    //     Planned RM Qty = 100 Persons QTY * Req. RM Qty / 100 Persons STD QTY
    //
    // In that order: the plan is worked out against the requirement this edit has just set, not
    // the one it replaced.
    //
    // The index is into the SORTED row list the table renders, so the write is matched back to
    // formData by ingredient_id rather than by position; the two orders are not the same.
    const handlePersonsQtyChange = (e, index) => {
        const { value } = e.target;
        const target = ingredientRows[index];
        if (!target) return;

        // Read through personsQty so that clearing the box falls back to the STD figure beside it
        // -- the same figure the box then displays -- rather than leaving the row on its old one.
        const f = personsQty({ ...target, persons_qty: value });
        const req = reqFromPersons(target, f);
        const l = plannedFromPersons(target, f, req);

        setFormData((prev) => prev.map((item) => {
          if (item.ingredient_id !== target.ingredient_id) return item;
          const next = { ...item, persons_qty: value };
          if (req !== null) next.req_rm_qty = Number(req.toFixed(3));
          if (l !== null) {
            next.planned_qty = Number(l.toFixed(3));
            // Reqd Qty / 100 kg, kept in step with Planned RM Qty the way a Planned RM Qty edit
            // keeps it -- it is that figure per 100 kgs, and other screens read it.
            if (estRM > 0) next.required_qty = parseFloat(((l * 100) / estRM).toFixed(2));
          }
          return next;
        }));
    };

    const handlePlannedQtyChange = (e, index) => {
        const { name, value } = e.target;

        if(name == 'receipe_remarks'){
          setFormData((prevFormData) => {
            const updatedFormData = prevFormData.map(item => 
              // Check if the item has the specified ingrid_id
              
              item.ingredient_id == 9090
                  ? { ...item, remarks: value, name: name} // Update the item
                  : item // Leave other items unchanged
          );
          return updatedFormData;
        });
        }else if(name == 'process_remarks'){
          setFormData((prevFormData) => {
            const updatedFormData = prevFormData.map(item => 
              // Check if the item has the specified ingrid_id
              
              item.ingredient_id == 9091
                  ? { ...item, remarks: value, name: name} // Update the item
                  : item // Leave other items unchanged
          );
          return updatedFormData;
        });
        }else if(name == 'test_remarks'){
          setFormData((prevFormData) => {
            const updatedFormData = prevFormData.map(item => 
              // Check if the item has the specified ingrid_id
              
              item.ingredient_id == 9092
                  ? { ...item, remarks: value, name: name} // Update the item
                  : item // Leave other items unchanged
          );
          return updatedFormData;
        });
        }else if(name == 'planning_remarks'){
          setFormData((prevFormData) => {
            const updatedFormData = prevFormData.map(item => 
              // Check if the item has the specified ingrid_id
              
              item.ingredient_id == 9093
                  ? { ...item, remarks: value, name: name} // Update the item
                  : item // Leave other items unchanged
          );
          return updatedFormData;
        });
        }else{
          // The index is into the SORTED row list the table renders, so the write is matched back
          // to formData by ingredient_id. It used to index straight into formData, which is in
          // database order and carries the four remarks rows as well -- so typing a Planned RM Qty
          // landed on whichever ingredient happened to sit at that position, which is rarely the
          // one that was typed in.
          const target = ingredientRows[index];
          if (!target) return;

          // What this edit does to one row:
          //
          //     100 Persons QTY = Planned RM Qty * 100 Persons STD QTY / Req. RM Qty
          //     Req. RM Qty     = Estimated RM Qty * 100 Persons QTY / Estimated Ras
          //
          // The requirement the first line divides by is the STANDARD one -- the row's own STD
          // figure through the second line -- not whatever Req. RM Qty is showing. It has to be a
          // fixed base, because the requirement moves with this edit: dividing by the figure the
          // last edit left would compound, and typing 30, then 33, then 30 again gave three
          // different requirements while retyping the same 30 moved the row on its own.
          const rowFields = (row, planned) => {
            const base = reqFromPersons(row, stdPersonsQty(row));
            const f = personsFromPlanned(row, parseFloat(planned), base);
            const rNext = reqFromPersons(row, f);
            const next = { planned_qty: planned };
            if (estRM > 0) {
              next.required_qty = parseFloat(((parseFloat(planned || 0) * 100) / estRM).toFixed(2));
            }
            if (rNext !== null) next.req_rm_qty = Number(rNext.toFixed(3));
            if (f !== null) next.persons_qty = String(Number(f.toFixed(3)));
            return next;
          };

          // A MAIN ingredient's Planned RM Qty carries the OTHER INGREDIENTS with it. The main
          // ingredient is what the batch is made of, so its plan is the size of the batch, and
          // each other ingredient keeps its share of that batch:
          //
          //     Planned RM Qty = (its own Planned RM Qty / Total of Planned RM Qty)
          //                      * the new Total of Planned RM Qty
          //
          // both totals read off the MAIN section, the second being what that total becomes once
          // the typed figure replaces the one it replaces. Which is to say: multiplied by new
          // total / old total.
          //
          // It scales each row's OWN PLAN, not its standard, so a row planned away from standard
          // stays planned away from standard. શક્કરીયાં 391.95 of a main total of 585, with
          // સિંગતેલ hand-raised to 70.2 against a standard 58.5, becomes 491.95 -- a new total of
          // 685 -- and સિંગતેલ goes to (70.2 / 585) * 685 = 82.200, still a fifth above its
          // standard. Rebuilt from the STD column it would have snapped back to 68.500.
          //
          // The OTHER MAINS are left alone. A recipe with two mains has two things the batch is
          // made of, and setting the quantity of one says nothing about the other -- only the
          // main typed into moves, along with the ingredients that season the batch as a whole.
          // That is also what makes the new total well defined: the old total, one row swapped.
          //
          // An OTHER ingredient's edit still touches its own row alone.
          const l = parseFloat(value);
          const plannedOf = (row) => {
            const p = parseFloat(row?.planned_qty);
            return isNaN(p) ? 0 : p;
          };
          const mainTotalBefore = ingredientRows
            .filter((r) => r.is_main)
            .reduce((t, r) => t + plannedOf(r), 0);
          const mainTotalAfter = mainTotalBefore - plannedOf(target) + (isNaN(l) ? 0 : l);
          const resizesRecipe = !!target.is_main && !isNaN(l) && mainTotalBefore > 0;
          const scale = resizesRecipe ? mainTotalAfter / mainTotalBefore : null;

          setFormData((prevFormData) => prevFormData.map((item) => {
            if (!item || REMARK_NAMES.includes(item.name)) return item;
            if (item.ingredient_id === target.ingredient_id) {
              const next = { ...item, [name]: value };
              return name === 'planned_qty' ? { ...next, ...rowFields(item, value) } : next;
            }
            if (!resizesRecipe || name !== 'planned_qty') return item;
            // Another main is a separate thing the batch is made of, not a share of this one.
            if (item.is_main) return item;
            const p = parseFloat(item?.planned_qty);
            // Nothing planned yet, so no share to carry -- leave the row rather than writing 0.
            if (isNaN(p)) return item;
            return { ...item, ...rowFields(item, Number((p * scale).toFixed(3))) };
          }));
        }
        
    };

  const handleOpenMaharajModal = (index) => {
    const meal = mealList[index];
    if (!meal || !meal.menu_item_id) {
      toast.error("Meal Master data not found for this item.");
      return;
    }
    setMaharajModalItem(meal);
    setSelectedMaharajId(meal.maharaj ? String(meal.maharaj) : '');
    setShowMaharajModal(true);
  };

  const handleSaveMaharaj = async () => {
    if (!maharajModalItem) return;
    try {
      setLoading(true);
      await axios.post(`${baseUrl}/meal_master/maharaj_update`, {
        date,
        session: session.value,
        menu_item_id: maharajModalItem.menu_item_id,
        maharaj_id: selectedMaharajId || null,
      });
      toast.success('Maharaj updated successfully');
      setShowMaharajModal(false);
      window.location.reload();
    } catch (err) {
      console.log(err);
      toast.error('Failed to update Maharaj');
      setLoading(false);
    }
  };


  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true)
    const menu_item_id =
      mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    const combineData = {
      formData: formData.filter((item) => item && item.name !== "receipe_remarks" && item.name !== "process_remarks" && item.name !== "test_remarks" && item.name !== "planning_remarks"),
      date: date,
      session: session,
      menu_item_id: menu_item_id,
    };
    console.log(combineData);
    try {
      // setPopupOpen(true);

      await axios.post(`${baseUrl}/production_planning/receipe`, combineData);
      if(formData.filter((item) => item && item.name == "planning_remarks")){
        handleSaveFeedback(e);
      }
      toast.success("Data successfully saved!");
      window.location.reload();
    } catch (error) {
      toast.error("Failed to submit the form.");
      // setPopupOpen(false);
      console.log(error);
    }
    setLoading(false)
  };

  const handleSaveFeedback = async (e) => {
    e.preventDefault();
    const menu_item_id =
      mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    const combineData = {
      formData: formData.filter((item) => item && item.name == "receipe_remarks" || item.name == "process_remarks" || item.name == "test_remarks" || item.name == "planning_remarks"),
      date: date,
      session: session,
      menu_item_id: menu_item_id,
    };
    console.log(combineData);
    try {
      // setPopupOpen(true);

      await axios.post(`${baseUrl}/production_planning/remarks`, combineData);
      toast.success("Data successfully saved!");
      
      window.location.reload();
    } catch (error) {
      toast.error("Failed to submit the form.");
      // setPopupOpen(false);
      console.log(error);
    }
  }

  const handleRecipeExport = async(e,index) => {
    const menu_item_id =
      mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    const response = await axios.post(`${baseUrl}/receipe/export`, {menu_item_id : menu_item_id});

    const data = response.data.data;

    const menu_item = mealList.find((item, i) => i === activeIndex);
    const itemName = menu_item?.item_name;
    const engName = menu_item?.eng_name;
    const itemDisplayName = itemName || engName ? `${itemName ?? ''}${engName ? ` (${engName})` : ''}`.trim() : '-';
    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Every sheet carries the screen's columns now, not just the recipe's four, and the sheets
    // stay one per maharaj. The building is in buildRecipeExportSheets so it reads through the
    // same arithmetic the table renders from -- see there for how an unplanned maharaj is filled.
    const sheets = buildRecipeExportSheets(data, {
        estRM,
        itemDisplayName,
        plannedMaharajId: mealList.find((item, i) => i === activeIndex)?.maharaj ?? null,
        plannedRows: formData,
        dayRas: rmDetailForItem?.[0]?.ras_qty_per_100_person,
    });

    sheets.forEach((sheet) => {
        const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
        ws['!cols'] = sheet.cols;
        XLSX.utils.book_append_sheet(wb, ws, sheet.name);
    });

    // Write the Excel file
    const fileName = `Recipe_Planning_${date}_${session.label}_${menu_item.item_name}.xlsx`;
    XLSX.writeFile(wb, fileName);
   
  }

  const handleRefreshIngredient = async (e) => {
    e.preventDefault();
    setLoading(true)
    const menu_item_id = mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    const maharajId = mealList.find((item, index) => index === activeIndex)?.maharaj ?? null;
   
    const obj = {
      menu_item_id : menu_item_id, 
      maharaj_id : maharajId,
      date: date,
      session:session
    }
    const response = await axios.post(`${baseUrl}/receipe/ingredient_refresh`, obj);
    
    const ingList = [];
    response.data.data.forEach((item) => {

        const data = ingredientList.find((v) => v.id == item.ingredient_id);
        const stdQty = parseFloat(item.qty);
        const _prmQtyRefresh = rmDetailForItem?.[0]?.planned_rm_qty ?? 0;
        const planned_qty = parseFloat((stdQty * _prmQtyRefresh / 100).toFixed(2));
        const dd = {ingredient_id : data.id, name : data.ingredient, unit: data.unit, stdQty: stdQty, required_qty : stdQty, baseQty: stdQty, store : data.store, planned_qty}
        ingList.push(dd);
    })
    const combineData = {
      formData: ingList,
      date: date,
      session: session,
      menu_item_id: menu_item_id,
    };
    console.log(combineData);
    try {
      // setPopupOpen(true);

      await axios.post(`${baseUrl}/production_planning/receipe`, combineData);
      if(formData.filter((item) => item && item.name == "planning_remarks")){
        handleSaveFeedback(e);
      }
      toast.success("Data successfully saved!");
      window.location.reload();
    } catch (error) {
      toast.error("Failed to submit the form.");
      // setPopupOpen(false);
      console.log(error);
    }

  }

  const handleExport = async (e, index) => {
    e.preventDefault();

    const menu_item = mealList.find((item, i) => i === activeIndex);
    const wb = XLSX.utils.book_new();

    // Group ingredients by store
    const ingredientsByStore = {};
    // console.log(formData)
    formData.forEach(item => {
        const storeName = item.store?.name || 'Main Store';
        if (!ingredientsByStore[storeName]) {
            ingredientsByStore[storeName] = [];
        }
        ingredientsByStore[storeName].push(item);
    });

    // Create sheets for each store
    Object.entries(ingredientsByStore).forEach(([storeName, items]) => {
        const ws = XLSX.utils.aoa_to_sheet([
            [""], // Placeholder for merged cell
            [],
            ["Requisition No."],
            ["Date :", date, "Session :", session.label, "Item :", menu_item.item_name],
            ["Batch :", formData[index].batch, "Vessel :", formData[index].vessel_id?.label, "Plan Batch Qty :", formData[index].planned_batch_raw_qty],
            [],
            [],
            ["Store:", storeName], // Add store name
            ["Sr. No.", "Ingredients", "Unit", "Required Qty", "Issued Qty.", "Excess Qty (support)", "Consumed Qty.", "Return to Stores (Qty.)"],
            ...items.map((item, idx) => [
                idx + 1,
                item.name,
                item.unit?.name,
                item.required_qty,
                "",
                "",
                "",
                ""
            ])
        ]);

        // Merge cells as needed
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
            { s: { r: 2, c: 1 }, e: { r: 2, c: 7 } },
            { s: { r: 3, c: 4 }, e: { r: 3, c: 5 } },
            { s: { r: 4, c: 4 }, e: { r: 4, c: 5 } },
            { s: { r: 3, c: 6 }, e: { r: 3, c: 7 } },
            { s: { r: 4, c: 6 }, e: { r: 4, c: 7 } },
            { s: { r: 6, c: 0 }, e: { r: 6, c: 7 } } // Merge store name cell
        ];

        // Set cell values and styles
        ws['A1'] = {
            v: "Raw Material Requisition & Issue Slip( Store / Support )",
            s: {
                alignment: { horizontal: "center" },
                font: { sz: 20, color: { rgb: "000000" } },
                fill: { patternType: "solid", fgColor: { rgb: "D8D8D8" } }
            }
        };

        ws['A3'] = { v: "Requisition No." };
        ws['B3'] = {
            v: formData[index].requisition_no,
            s: {
                alignment: { horizontal: "center" },
                font: { sz: 12, color: { rgb: "000000" } }
            }
        };

        ws['G4'] = { v: menu_item.item_name };
        ws['G5'] = { v: formData[index].planned_batch_raw_qty };

        const wscols = [
            { wch: 10 }, // Reduced width for Date and Batch columns
            { wch: 10 }, // Requisition No. column
            { wch: 10 }, // Date column
            { wch: 10 }, // Session column
            { wch: 10 }, // Item column
            { wch: 10 }, // Batch column
            { wch: 10 }, // Vessel column
            { wch: 10 }, // Plan Batch Qty column
            { wch: 5 },  // Ingredient ID column
            { wch: 10 }, // Name column
            { wch: 5 },  // Unit column
            { wch: 10 }, // Std Qty column
            { wch: 10 }, // Planned Qty column
        ];
        ws['!cols'] = wscols;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, `Store_${storeName}`);
    });

    // Generate XLSX file and trigger download
    const finleName =  `Recipe_Planning_${date}_${session.label}_${menu_item.item_name}.xlsx`;
    console.log(finleName);
    XLSX.writeFile(wb, finleName);
};

// The modal's Save. It writes the ingredient before reloading, which the spec asks for -- adding
// it to formData alone would have been undone by the refresh a moment later.
//
// Only the new row is sent. The endpoint upserts by ingredient_id, so the rows already on the
// screen are recognised and left exactly as they are; sending them all would rewrite quantities
// the user may be part-way through editing.
const handleAddIngredient = async (ingredient, plannedQty, isMain = false) => {
    if (!ingredient?.value) return;
    const menu_item_id = mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    // The ingredient is in no master recipe, so it comes with no qty per 100 kgs -- the figure
    // "100 Persons STD QTY" is read from, and the one the rest of the row is measured against.
    // The Planned RM Qty typed into the modal is the standard the planner has just set for it, so
    // it becomes the row's own: the same per-100-kgs conversion a Planned RM Qty edit already
    // makes on every other row. Left blank, the row is added at zero and can still be typed into.
    const planned = parseFloat(plannedQty) || 0;
    const perHundredKg = estRM > 0 ? parseFloat(((planned * 100) / estRM).toFixed(2)) : 0;
    const ob = {
      ingredient_id : ingredient.value,
      name: ingredient.name,
      unit:ingredient.data.unit,
      stdQty: perHundredKg,
      required_qty: perHundredKg,
      planned_qty: planned,
      baseQty:0,
      store: ingredient.data.store,
      is_main: !!isMain
    };
    setLoading(true);
    try {
      await axios.post(`${baseUrl}/production_planning/receipe`, {
        formData: [ob],
        date: date,
        session: session,
        menu_item_id: menu_item_id,
      });
      toast.success("Ingredient added!");
      window.location.reload();
    } catch (error) {
      setLoading(false);
      toast.error("Failed to add the ingredient.");
      console.log(error);
    }
} 

// The buttons above an open panel all open a new tab for the item that panel is showing. A new
// tab carries no router state, so what they are about travels in the query string -- the reports
// read it through Filter/reportLink.js, and Recipe Master through its own list filters.
const activeMenuItemId = () => mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;

const openRecipeFeedbackReport = () => {
  window.open(`/reports/receipe_feedback${reportLinkQuery(activeMenuItemId())}`, "_blank", "noopener,noreferrer");
}

const openIngredientConsumedReport = () => {
  window.open(`/reports/ingredients${reportLinkQuery(activeMenuItemId())}`, "_blank", "noopener,noreferrer");
}

const openRecipeMaster = () => {
  const menu_item_id = activeMenuItemId();
  const maharajId = maharajDetail?.id ?? mealList.find((item, i) => i == activeIndex)?.maharaj ?? '';
  window.open(`/receipe/list?item=${menu_item_id}${maharajId ? `&maharaj=${maharajId}` : ''}`, "_blank", "noopener,noreferrer");
}

const handleProcessedit = (e) => {
e.preventDefault();
const menu_item_id =
      mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;

// A new tab, per the spec, which is why the maharaj travels in the query string: router state
// does not survive window.open, and EditReceipe rebuilds its subject from the URL when it
// arrives without any.
const maharajId = maharajDetail?.id ?? '';
window.open(`/receipe/edit/${btoa(menu_item_id)}${maharajId ? `?maharaj=${maharajId}` : ''}`, "_blank", "noopener,noreferrer");
}


  return (
    <div className="">
      {loading && <Loader/>}
      <div className="flex justify-between items-center py-4 px-6">
        <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
          Recipe Planning
        </h1>
        <Toaster position="top-center" reverseOrder={false} />
        {/* <div>
                    <Link to="/menu/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Menuitem</button>
                    </Link>
                </div> */}
      </div>
      <div className="relative  shadow-md sm:rounded-lg">
        <DateSessionFilter handleFilterChange={handelFilterChange} />
        <div>
          {mealList.map((item, index) => (
            <div key={index} className="border-b mt-4">
              <div
                className="flex justify-between items-center p-4 cursor-pointer accordion-title bg-orange-400 border rounded"
                onClick={() => handleClick(index)}
              >
                <span><ItemNameStacked item={item} /></span>
                {index === activeIndex ? (
                  <ChevronUpIcon className="w-6 h-6" />
                ) : (
                  <ChevronDownIcon className="w-6 h-6" />
                )}
              </div>
              {index === activeIndex && (
                <div className="space-y-2 p-4 bg-white">
                  <div className="flex justify-between">
                    <div className="lg:w-96">
                      
                      
                      <div className="flex flex-wrap justify-between">
                        <div className="sm:text-sm lg:text-base font-bold">
                        Estimated Count (Persons):
                        </div>
                        <div>{rmDetailForItem?.[0]?.estimated_count ?? 0}</div>
                      </div>
                      <div className="flex flex-wrap justify-between">
                        <div className="sm:text-sm lg:text-base font-bold">
                          Estimated Ras (100 Persons):
                        </div>
                        <div>{rmDetailForItem?.[0]?.ras_qty_per_100_person ?? 0}</div>
                      </div>
                      <div className="flex flex-wrap justify-between">
                        <div className="sm:text-sm lg:text-base font-bold">
                          Estimated RM Qty (kgs):
                        </div>
                        <div>{rmDetailForItem?.[0]?.planned_rm_qty ?? 0}</div>
                      </div>
                      <div className="flex flex-wrap justify-between">
                        <div className="sm:text-sm lg:text-base font-bold">
                        Maharaj Name:
                        </div>
                        <div>{maharajName ?? '-'}</div>
                      </div>
                      <div className="flex justify-between">
                        <div className="sm:text-sm lg:text-base font-bold">
                        Planning Remarks:
                        </div>
                        <div>
                        <textarea
                            name="planning_remarks"
                            id="remarks"
                            placeholder='Remarks'
                            value={formData.find(item => item.name === 'planning_remarks')?.remarks || ''}
                            onChange={(e) => handlePlannedQtyChange(e, 9093)}
                            className="block w-full rounded-md border py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            rows="4" // Adjust the number of rows as needed
                          />
                        </div>
                      </div>
                    </div>
                    <div className="justify-end">
                      <button
                        onClick={openRecipeFeedbackReport}
                        disabled={REPORT_BUTTONS_DISABLED}
                        className={`px-4 py-2 text-white rounded-md placeholder:text-sm mr-2 float-right ${REPORT_BUTTONS_DISABLED ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500'}`}>
                        Recipe Feedback Report
                      </button>
                    </div>
                    <div className="justify-end">
                      <button
                        onClick={openRecipeMaster}
                        className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right">
                        Recipe Master
                      </button>
                    </div>
                    <div className="justify-end">
                      <button
                        onClick={openIngredientConsumedReport}
                        disabled={REPORT_BUTTONS_DISABLED}
                        className={`px-4 py-2 text-white rounded-md placeholder:text-sm mr-2 float-right ${REPORT_BUTTONS_DISABLED ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500'}`}>
                        Ingredient Consumed Report
                      </button>
                    </div>
                    <div className="justify-end">
                      <button
                        className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2 float-right"
                        onClick={() => handleOpenMaharajModal(index)}
                      >
                        Change Maharaj
                      </button>
                    </div>
                    {/* <div className="justify-end">
                        <button onClick={(e) => { handleProcessedit(e)}} className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right">
                          Add Ingredient to Recipe
                        </button>
                    </div> */}
                    {/* {!itemDisable && <div className="justify-end">
                      <Link to="#" 
                      onClick={() => window.open("/ingredients/add", "_blank", "noopener,noreferrer")}
                      >
                        <button className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right">
                        Add Ingredient to Master
                        </button>
                      </Link>
                    </div>} */}
                  </div>
                  {/* `showModal`, not `showModalplanning_remarks` -- the modal reads `showModal`,
                      so under the old name it was handed undefined and never opened. */}
                  <ModalContent showModal={showModal} setShowModal={setShowModal} ingredientList={ingredientList} handleAddIngredient={handleAddIngredient} ingredientDetail={formData} showPlannedQty showMainItem />
                  {showMaharajModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-bold mb-4">Change Maharaj</h3>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Menuitem</label>
                          <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900">
                            {maharajModalItem ? `${maharajModalItem.item_name}${maharajModalItem.eng_name ? ` (${maharajModalItem.eng_name})` : ''}` : '-'}
                          </div>
                        </div>
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Maharaj Name</label>
                          <select
                            value={selectedMaharajId}
                            onChange={(e) => setSelectedMaharajId(e.target.value)}
                            className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">-- Select Maharaj --</option>
                            {maharajList.map((m) => (
                              <option key={m.id} value={m.id}>{m.maharaj_name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowMaharajModal(false)}
                            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveMaharaj}
                            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex overflow-x-auto">
                    
                      <div className="gap-5 mb-4 mt-10 ms-2 flex-shrink-0 ">
                       
                        {formData.length > 0 && (() => {
                            // The figures and the formulas both come from the component scope
                            // above, which is also where handlePlannedQtyChange reads them, so an
                            // edit and the table it redraws cannot be working from two different
                            // definitions of the same column.
                            const dec3 = (v) => (v === null || v === undefined || isNaN(v) ? '' : Number(v).toFixed(3));

                            const mainRows  = ingredientRows.filter(r => r.is_main);
                            const otherRows = ingredientRows.filter(r => !r.is_main);
                            // % Weightage divides by the STD total of its OWN section, which is what
                            // the sheet's figures do -- the main rows and the other rows are each
                            // weighed against their own total, so at standard each set sums to 100%.
                            const sumStd = (list) => list.reduce((t, r) => t + (stdPersonsQty(r) ?? 0), 0);
                            const sum = (list, fn) => list.reduce((t, r) => t + (fn(r) ?? 0), 0);
                            const weightage = (row, total) => {
                              const f = personsQty(row);
                              if (f === null || !total) return '';
                              return `${((f / total) * 100).toFixed(2)}%`;
                            };
                            // The Total row read a flat 100.00%, which is only true while every
                            // 100 Persons QTY still sits at its STD figure. Once one is typed in
                            // -- or moved by a Planned RM Qty edit -- the column no longer sums to
                            // 100, and a Total that says otherwise hides exactly the change the
                            // planner just made. It adds up the column it heads instead.
                            const weightageTotal = (list, total) => {
                              if (!total) return '';
                              return `${((sum(list, personsQty) / total) * 100).toFixed(2)}%`;
                            };

                            // One row per ingredient, and one subtotal row per section. COL_COUNT is
                            // the single source of truth for how wide the section bands span, so the
                            // header, the bands and the body cannot drift apart.
                            const COL_COUNT = 8;
                            const renderSection = (title, list) => {
                              const sectionStdTotal = sumStd(list);
                              return (
                                <>
                                  <tr className="bg-gray-200 font-bold">
                                    <td className="text-left px-3 py-1" colSpan={COL_COUNT}>{title}</td>
                                  </tr>
                                  {list.map((item) => {
                                    const idx = ingredientRows.indexOf(item);
                                    return (
                                      <tr key={item.ingredient_id} className={`border ${item.is_main ? 'bg-blue-50' : ''}`}>
                                        <td className="text-center">{item.name}</td>
                                        <td className="text-center">{item.unit?.name ?? "-"}</td>
                                        <td className="text-center">
                                          <input type="checkbox" checked={item.is_main || false} onChange={(e) => handleIsMainChange(item.ingredient_id, e.target.checked)} className="w-4 h-4 accent-blue-500 cursor-pointer" />
                                        </td>
                                        <td className="text-center p-1">{dec3(stdPersonsQty(item))}</td>
                                        <td className="text-center p-1">
                                          <input type="number" step="0.001" onWheel={(e) => e.target.blur()}
                                            onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                                            id={`persons_qty_${idx}`} name="persons_qty" disabled={itemDisable}
                                            placeholder="100 Persons QTY"
                                            value={item.persons_qty ?? (stdPersonsQty(item) === null ? '' : dec3(stdPersonsQty(item)))}
                                            onChange={(e) => handlePersonsQtyChange(e, idx)}
                                            className={`w-24 p-1 border rounded-md placeholder:text-sm ${itemDisable ? "" : "bg-orange-50"}`} />
                                        </td>
                                        <td className="text-center p-1">{weightage(item, sectionStdTotal)}</td>
                                        <td className="text-center p-1">{dec3(reqRmQty(item))}</td>
                                        <td className="text-center p-1">
                                          <input type="number" onWheel={(e) => e.target.blur()}
                                            onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                                            step="0.01" id={`planned_qty_${idx}`} disabled={itemDisable}
                                            placeholder="Planned RM Qty" name="planned_qty"
                                            value={item.planned_qty ?? 0} onChange={(e) => handlePlannedQtyChange(e, idx)}
                                            className={`w-24 p-1 border rounded-md placeholder:text-sm ${itemDisable ? "" : "bg-orange-50"}`} />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="border bg-gray-100 font-bold">
                                    <td className="text-center px-3" colSpan="3">Total</td>
                                    <td className="text-center px-3">{dec3(sectionStdTotal)}</td>
                                    <td className="text-center px-3">{dec3(sum(list, personsQty))}</td>
                                    <td className="text-center px-3">{weightageTotal(list, sectionStdTotal)}</td>
                                    <td className="text-center px-3">{dec3(sum(list, reqRmQty))}</td>
                                    <td className="text-center px-3">{sum(list, (r) => parseFloat(r.planned_qty ?? 0)).toFixed(3)}</td>
                                  </tr>
                                </>
                              );
                            };

                            return (
                            <div className="border p-2">
                            <table className="border space-x-1">
                              <thead className="space-x-1">
                                <tr>
                                  <th className="px-3">Main Ingredient</th>
                                  <th className="px-3">Unit</th>
                                  <th className="px-3">Main Item</th>
                                  <th className="px-3">100 Persons STD QTY</th>
                                  <th className="px-3">100 Persons QTY</th>
                                  <th className="px-3">% Weightage</th>
                                  <th className="px-3">Req. RM Qty</th>
                                  <th className="px-3">Planned RM Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mainRows.length > 0 && renderSection('Main Ingredients', mainRows)}
                                {otherRows.length > 0 && renderSection('Other Ingredients', otherRows)}
                              </tbody>
                            </table>
                            </div>
                            );
                          })()}
                        <div className="border p-2">
                        {!itemDisable && <>
                          <button
                            disabled={itemDisable}
                            onClick={(e) => { handleSave(e)}}
                            className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right"
                          >
                            Save
                          </button>
                          <button 
                       onClick={(e) => {
                        handleRefreshIngredient(e);
                      }}
                        className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right"
                      >
                        Refresh
                      </button>
                          <button
                        onClick={() => setShowModal(true)}
                        disabled={itemDisable}
                        className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right"
                      >
                        Add Ingredient
                      </button>
                      </>}
                          <button
                            onClick={(e) => {
                              handleRecipeExport(e, index);
                            }}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2"
                          >
                            Export
                          </button>
                          
                        </div>
                      </div>
                      <div className="gap-5 px-10">
                      <div className="grid grid-cols-1 gap-y-4">
                        <div className="mt-2 ">
                          <label htmlFor="field2" className="mr-2 w-full sm:w-auto md:w-52">Recipe Ingrd. Feedback</label>
                          <textarea
                            name="receipe_remarks"
                            id="remarks"
                            placeholder='FeedBack'
                            value={formData.find(item => item.name === 'receipe_remarks')?.remarks || ''}
                            onChange={(e) => handlePlannedQtyChange(e, 9090)}
                            className="block w-full rounded-md border py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            rows="4" // Adjust the number of rows as needed
                          />
                        </div>
                        <div className="mt-2">
                          <label htmlFor="field2" className="mr-2 w-full sm:w-auto md:w-52">Production Process Feedback</label>
                          <textarea
                            name="process_remarks"
                            id="remarks"
                            placeholder='FeedBack'
                            value={formData.find(item => item.name === 'process_remarks')?.remarks || ''}
                            onChange={(e) => handlePlannedQtyChange(e, 9091)}
                            className="block w-full rounded-md border py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            rows="4" // Adjust the number of rows as needed
                          />
                        </div>
                        <div className="mt-2">
                          <label htmlFor="field2" className="mr-2 w-full sm:w-auto md:w-52">Taste Feedback</label>
                          <textarea
                            name="test_remarks"
                            id="remarks"
                            placeholder='FeedBack'
                            value={formData.find(item => item.name === 'test_remarks')?.remarks || ''}
                            onChange={(e) => handlePlannedQtyChange(e, 9092)}
                            className="block w-full rounded-md border py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            rows="4" // Adjust the number of rows as needed
                          />
                        </div>
                        </div>
                        <div className=" p-2">
                          
                          <button
                            onClick={(e) => { handleSaveFeedback(e)}}
                            className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm mr-2 float-right"
                          >
                            Save FeedBack
                          </button>
                         
                          
                          
                        </div>
                      </div>
                      
                  </div>
                  <div className="p-6 bg-white rounded-lg shadow-md relative">
                    <div className="flex space-x-4 items-center mb-4">
                      <h3 className="text-xl font-semibold">Std. Process</h3>
                      {/* "editable for Recipe team POC only". There is no role that names the
                          recipe team -- users.role is a bare integer -- so this uses the roles
                          that can already reach Recipe Master (/receipe/list is allowedRoles [1],
                          plus superadmin, which HelmetWrapper lets through everywhere). Anyone
                          else reads the process without the way in to change it. */}
                      {canEditRecipe && <button
                      onClick={(e) => { handleProcessedit(e)}}
                      className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm">
                          Edit Process
                      </button>} 
                    </div>
  
  <div className="space-y-3">
    {processStep.length > 0 ? (
      processStep.map((item, index) => (
        <div key={index} className="p-3 border rounded-md bg-gray-50">
          <span className="font-medium">Step {index + 1}:</span> {item.process}
        </div>
      ))
    ) : (
      <div className="text-gray-500">No process steps available.</div>
    )}
  </div>
</div>

                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReceipePlanning;
